import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';

import { StoreAndLanguage } from '@core/domain/state/core.state';
import { HttpRequestsService, RequestParams } from '@core/infrastructure/http-requests/http-requests.service';
import { getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { ApiAddress, ApiAddresses, ApiAddressServiceInterface } from '@features/address';

@Injectable({ providedIn: 'root' })
export class ApiAddressService implements ApiAddressServiceInterface {
    constructor(
        private readonly http: HttpClient,
        private readonly requestService: HttpRequestsService,
        private readonly store: Store,
    ) {}

    public getAddresses(requestParameters: RequestParams): Observable<ApiAddresses> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `user/store/${storeAndLanguage.storeId}/address`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId, requestParameters);
                return this.http.get<ApiAddresses>(url);
            }),
        );
    }

    public saveAddress(address: any): Observable<any> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),

            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `user/store/${storeAndLanguage.storeId}/address`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId);
                return this.http.post(url, address);
            }),
        );
    }

    public deleteAddress(address: any): Observable<any> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `user/store/${storeAndLanguage.storeId}/address/${address.id}`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId);
                return this.http.delete(url);
            }),
        );
    }

    public getAddress(addressId: string): Observable<ApiAddress> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap(({ storeId, languageId }) => {
                const slug: string = `user/store/${storeId}/address/${addressId}`;
                const url: string = this.requestService.requestUrl(slug, languageId);
                return this.http.get<any>(url);
            }),
        );
    }

    public getAddressGuest(addressId: string): Observable<ApiAddress> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap(({ storeId, languageId }) => {
                const slug: string = `user/store/${storeId}/address-guest/${addressId}`;
                const url: string = this.requestService.requestUrl(slug, languageId);
                return this.http.get<any>(url);
            }),
        );
    }

    public getCountries(): Observable<any> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `bam/store/${storeAndLanguage.storeId}/country`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId);
                return this.http.get<any>(url);
            }),
        );
    }

    public getStateAddresses(storeId?: string): Observable<any> {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = storeId ? `bam/store/${storeId}/state` : `bam/store/${storeAndLanguage.storeId}/state`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId);
                return this.http.get<any>(url);
            }),
        );
    }

    public getCityAddresses(stateCode: string): Observable<any> {
        const customParams = [{ key: 'stateCode', value: stateCode }];
        return this.store.select(getStoreAndLanguage).pipe(
            first(),

            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `bam/store/${storeAndLanguage.storeId}/cities`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId, customParams);
                return this.http.get<any>(url);
            }),
        );
    }

    public getDistrictAddresses(cityIdAndName: string): Observable<any> {
        const cityId: string = cityIdAndName.split('#')[0];
        const customParams = [{ key: 'cityId', value: cityId }];
        return this.store.select(getStoreAndLanguage).pipe(
            first(),

            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `bam/store/${storeAndLanguage.storeId}/districts`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId, customParams);
                return this.http.get<any>(url);
            }),
        );
    }

    public getConfigAddress() {
        return this.store.select(getStoreAndLanguage).pipe(
            first(),
            switchMap((storeAndLanguage: StoreAndLanguage) => {
                const slug: string = `store/${storeAndLanguage.storeId}/config/address`;
                const url: string = this.requestService.requestUrl(slug, storeAndLanguage.languageId);
                return this.http.get<any>(url);
            }),
        );
    }
}
